import { apiFetch } from "../api.js";
import type {
  AccountHoldingsResponse,
  AccountSummary,
  AccountsListResponse,
  CreateAccountInput,
  CreateLotInput,
  CreateSaleInput,
  CreateDripInput,
  SaleResult,
  UpdateAccountInput,
  UpdateLotInput,
} from "./types.js";

/**
 * Accounts API module — Phase 3 Session 2 promotes from read-only to
 * full CRUD on accounts + lots.
 *
 * - `listAccounts` / `getAccountHoldings` — Phase 3 Session 1 (read)
 * - `createAccount` / `updateAccount` / `archiveAccount` — Phase 3.2
 * - `createLot` / `updateLot` / `deleteLot` — Phase 3.2
 *
 * All endpoints JWT-authed; household resolved from the JWT, so the
 * frontend never passes household_id. Account-scoped lot endpoints
 * also enforce `assertAccountAccess` server-side.
 *
 * Soft-delete on accounts (returns 204; row stays in DDB with
 * is_archived=true). Hard-delete on lots (lots are leaf rows; the
 * audit log carries the history).
 */

export function listAccounts(): Promise<AccountsListResponse> {
  return apiFetch<AccountsListResponse>("/v1/accounts");
}

export function getAccountHoldings(
  accountId: string,
): Promise<AccountHoldingsResponse> {
  return apiFetch<AccountHoldingsResponse>(
    `/v1/accounts/${encodeURIComponent(accountId)}/holdings`,
  );
}

export function createAccount(
  input: CreateAccountInput,
): Promise<AccountSummary> {
  return apiFetch<AccountSummary>("/v1/accounts", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function updateAccount(
  accountId: string,
  input: UpdateAccountInput,
): Promise<AccountSummary> {
  return apiFetch<AccountSummary>(
    `/v1/accounts/${encodeURIComponent(accountId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(input),
    },
  );
}

export function archiveAccount(accountId: string): Promise<void> {
  return apiFetch<void>(`/v1/accounts/${encodeURIComponent(accountId)}`, {
    method: "DELETE",
  });
}

export function createLot(
  accountId: string,
  input: CreateLotInput,
): Promise<unknown> {
  return apiFetch<unknown>(
    `/v1/accounts/${encodeURIComponent(accountId)}/lots`,
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
}

export function updateLot(
  accountId: string,
  lotId: string,
  input: UpdateLotInput,
): Promise<unknown> {
  return apiFetch<unknown>(
    `/v1/accounts/${encodeURIComponent(accountId)}/lots/${encodeURIComponent(lotId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(input),
    },
  );
}

export function deleteLot(accountId: string, lotId: string): Promise<void> {
  return apiFetch<void>(
    `/v1/accounts/${encodeURIComponent(accountId)}/lots/${encodeURIComponent(lotId)}`,
    { method: "DELETE" },
  );
}

/**
 * `POST /v1/accounts/{account_id}/transactions` — sale.
 *
 * Returns the SaleResult with per-lot reductions + realized PnL + LT/ST
 * split for the SPA toast. Errors that bubble up: 422
 * `UNPROCESSABLE_ENTITY` with `{code: "INSUFFICIENT_QUANTITY" | ...}`
 * for cost-basis-math failures; 403 `ROLE_DENIED` for viewers.
 */
export function createSale(
  accountId: string,
  input: CreateSaleInput,
): Promise<SaleResult> {
  return apiFetch<SaleResult>(
    `/v1/accounts/${encodeURIComponent(accountId)}/transactions`,
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
}

/**
 * `POST /v1/accounts/{account_id}/transactions` — DRIP. Synthesizes a
 * lot with `source: "drip"`. Phase 11 owns the SPA UI; this wrapper
 * exists for completeness and Andre's curl/aws-lambda-invoke smoke.
 */
export function createDrip(
  accountId: string,
  input: CreateDripInput,
): Promise<{ transaction: Record<string, unknown>; lot: Record<string, unknown> }> {
  return apiFetch(
    `/v1/accounts/${encodeURIComponent(accountId)}/transactions`,
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
}
